/**
 * Centralized Logger Utility
 *
 * Unified logging that sends errors to PostHog in production while keeping
 * console output in development. Safe to use in both client and server components.
 *
 * Usage:
 * ```typescript
 * import { logger } from '@/lib/utils/logger';
 *
 * // Debug - dev console only
 * logger.debug('Processing started', { action: 'getUsers' });
 *
 * // Info - dev console only
 * logger.info('User created', { userId: '123' });
 *
 * // Warn - dev console only
 * logger.warn('Rate limit approaching', { remaining: 10 });
 *
 * // Error - console in dev, PostHog (client) or console (server) in production
 * logger.error('Failed to fetch users', error, { endpoint: '/api/users' });
 * ```
 *
 * NOTE: This logger is client-safe. It uses posthog-js on the client and
 * console.error on the server. For server-side PostHog tracking, use the
 * PostHogClient from @/app/posthog directly in server-only code.
 */

import { LOG_ERROR } from "@/lib/analytics/events";

// ============================================================================
// Types
// ============================================================================

type LogLevel = "debug" | "info" | "warn" | "error";
type LogContext = Record<string, unknown>;

// ============================================================================
// Logger Class
// ============================================================================

class Logger {
  private readonly isDev = process.env.NODE_ENV === "development";
  private posthogJs: typeof import("posthog-js").default | null = null;

  /**
   * Lazily load posthog-js only on the client side
   */
  private async getPostHogJs() {
    if (typeof globalThis.window === "undefined") {
      return null;
    }
    if (!this.posthogJs) {
      try {
        const mod = await import("posthog-js");
        this.posthogJs = mod.default;
      } catch {
        return null;
      }
    }
    return this.posthogJs;
  }

  /**
   * Internal log method that handles both console and PostHog output
   */
  private log(
    level: LogLevel,
    message: string,
    context?: LogContext,
    error?: Error,
  ): void {
    const prefix = {
      debug: "[DEBUG]",
      info: "[INFO]",
      warn: "[WARN]",
      error: "[ERROR]",
    }[level];
    const consoleMethod = level === "debug" ? "log" : level;

    // Development: Always log to console
    if (this.isDev) {
      console[consoleMethod](`${prefix}`, message, context ?? "");
      if (error) {
        console.error(error);
      }
      return;
    }

    // Production: Only send errors to PostHog (client) or console (server)
    if (level !== "error") {
      return;
    }

    // Server-side: Just use console.error (PostHog tracking should use server client directly)
    if (typeof globalThis.window === "undefined") {
      console.error(`${prefix}`, message, context ?? "");
      if (error) {
        console.error(error);
      }
      return;
    }

    // Client-side: Use posthog-js
    this.captureClientError(message, error, context);
  }

  /**
   * Capture error on client-side using posthog-js
   */
  private captureClientError(
    message: string,
    error?: Error,
    context?: LogContext,
  ): void {
    void this.getPostHogJs().then((posthog) => {
      if (!posthog) return;
      try {
        posthog.capture(LOG_ERROR, {
          message,
          error_message: error?.message,
          error_name: error?.name,
          error_stack: error?.stack,
          url: globalThis.window?.location?.href,
          ...context,
        });
      } catch {
        // Silently fail - don't break the app if PostHog fails
      }
    });
  }

  /**
   * Debug log - development console only, never sent to PostHog
   */
  debug(message: string, context?: LogContext): void {
    this.log("debug", message, context);
  }

  /**
   * Info log - development console only, never sent to PostHog
   */
  info(message: string, context?: LogContext): void {
    this.log("info", message, context);
  }

  /**
   * Warning log - development console only, never sent to PostHog
   */
  warn(message: string, context?: LogContext): void {
    this.log("warn", message, context);
  }

  /**
   * Error log - console in development, PostHog (client) or console (server) in production
   */
  error(message: string, error?: Error, context?: LogContext): void {
    this.log("error", message, context, error);
  }
}

// ============================================================================
// Export Singleton
// ============================================================================

export const logger = new Logger();
